import { useEffect, useMemo, useRef, useState } from "react";
import { normalizePhone, validateOtpCode, validatePhoneForOtp } from "./otpValidation";

export const OTP_RESEND_COUNTDOWN_SECONDS = 30;

export default function useOtpFlow({ initialPhone = "", t }) {
  const [phone, setPhone] = useState(initialPhone);
  const [otp, setOtp] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setPhone(initialPhone);
  }, [initialPhone]);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    timerRef.current = window.setTimeout(() => {
      setResendCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [resendCountdown]);

  const resendDisabled = useMemo(
    () => isSendingOtp || resendCountdown > 0,
    [isSendingOtp, resendCountdown]
  );

  const validatePhone = (value = phone) => {
    const error = validatePhoneForOtp(value, t);
    setPhoneError(error);
    return error;
  };

  const validateOtp = (value = otp) => {
    const error = validateOtpCode(value, t);
    setOtpError(error);
    return error;
  };

  const startCountdown = (seconds = OTP_RESEND_COUNTDOWN_SECONDS) => {
    setResendCountdown(seconds);
  };

  const resetOtpError = () => setOtpError("");
  const resetPhoneError = () => setPhoneError("");

  useEffect(() => {
    if (phoneError) {
      setPhoneError((prev) => validatePhoneForOtp(phone, t) || prev);
    }
    if (otpError) {
      setOtpError((prev) => validateOtpCode(otp, t) || prev);
    }
  }, [otp, otpError, phone, phoneError, t]);

  return {
    phone,
    setPhone,
    otp,
    setOtp,
    phoneError,
    setPhoneError,
    otpError,
    setOtpError,
    isSubmitting,
    setIsSubmitting,
    isSendingOtp,
    setIsSendingOtp,
    resendCountdown,
    resendDisabled,
    validatePhone,
    validateOtp,
    startCountdown,
    resetOtpError,
    resetPhoneError,
    normalizedPhone: normalizePhone(phone),
  };
}
